import { useState } from 'react';
import { Star, Mail, Phone, Calendar, Clock, Briefcase, MapPin, } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { apiErrorMessage } from '@shared/api/client';
import {
  formatAdminRole, type AdminRole, type AdminUserRow, type UserRole, type UserStatus, type Uuid,
} from '@shared/types/api';
import {
  useAdminUserFeedbacks, useAdminUsers, useReinstateUser, useSuspendUser,
  useDeleteUser,
} from '@/hooks/use-admin';
import { useSectionPermission } from '@/hooks/use-admin-permissions';
import { useMe } from '@/hooks/use-me';
import { useAuthStore } from '@shared/stores/auth-store';

const ROLE_TABS: { value: UserRole | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'CLIENT', label: 'Clients' },
  { value: 'ASSOCIATE', label: 'Associates' },
  { value: 'FRANCHISE', label: 'Franchises' },
  { value: 'ADMIN', label: 'Admins' },
];

const STATUS_TONE: Record<UserStatus, string> = {
  PENDING_EMAIL_VERIFICATION: 'bg-gray-100 text-gray-700',
  PENDING_ADMIN_APPROVAL: 'bg-amber-50 text-amber-800',
  ACTIVE: 'bg-brand-50 text-brand-800',
  SUSPENDED: 'bg-red-50 text-red-700',
  REJECTED: 'bg-gray-200 text-gray-700',
};

function getUserRoleDisplay(user: { role: UserRole; adminRole?: AdminRole | null }): string {
  if (user.role === 'ADMIN') {
    return formatAdminRole(user.adminRole);
  }
  if (user.role === 'ASSOCIATE') return 'Associate';
  if (user.role === 'FRANCHISE') return 'Franchise';
  if (user.role === 'CLIENT') return 'Client';
  return user.role;
}

const PROFESSION_LABELS: Record<string, string> = {
  LAWYER: 'Lawyer',
  CHARTERED_ACCOUNTANT: 'Chartered Accountant',
  COMPANY_SECRETARY: 'Company Secretary',
  COST_MANAGEMENT_ACCOUNTANT: 'Cost & Management Accountant',
  DOCUMENT_WRITER: 'Document Writer',
  TAX_CONSULTANT: 'Tax Consultant',
  OTHER_DEGREE_HOLDER: 'Other / Degree Holder',
};

// ---- Inline star display -----------------------------------------
function InlineStars({ value, size = 'sm' }: { value: number; size?: 'xs' | 'sm' | 'md' }) {
  const filled = Math.round(value);
  const sizeClass = size === 'xs' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={cn(
            sizeClass,
            filled >= s ? 'fill-amber-400 text-amber-400' : 'fill-gray-100 text-gray-300',
          )}
          strokeWidth={1.5}
        />
      ))}
    </span>
  );
}

// ---- Associate details dialog ------------------------------------
function AssociateDetailsDialog({
  user,
  onClose,
}: {
  user: AdminUserRow | null;
  onClose: () => void;
}) {
  const isAssociate = user?.role === 'ASSOCIATE' || user?.role === 'FRANCHISE';
  const { data: feedbacks = [], isLoading: loadingFeedbacks } = useAdminUserFeedbacks(
    isAssociate ? (user?.id ?? null) : null,
  );

  if (!user) return null;

  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  const initials =
    ((user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '')).toUpperCase() ||
    user.email.slice(0, 2).toUpperCase();

  const ap = user.associateProfile;

  // Compute average rating from feedbacks
  const avgRating =
    feedbacks.length > 0
      ? feedbacks.reduce((sum, f) => sum + f.rating, 0) / feedbacks.length
      : null;

  return (
    <Dialog
      open
      onClose={onClose}
      className={cn('w-full', isAssociate ? 'max-w-4xl max-h-[90vh]' : 'max-w-xl max-h-[90vh]')}
      title={
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900">
            {isAssociate ? 'Associate Profile & Performance' : 'User Account Details'}
          </span>
        </div>
      }
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-5">
        {/* Top Header Card */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-gradient-to-r from-gray-50 via-slate-50 to-emerald-50/40 border border-gray-200/80">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-brand-700 text-white font-bold text-base flex items-center justify-center shrink-0 shadow-xs">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-bold text-gray-900 truncate">{displayName}</h3>
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold tracking-wider',
                    user.adminRole === 'SUPER_ADMIN'
                      ? 'bg-purple-100/80 text-purple-800 border border-purple-200'
                      : user.role === 'ADMIN'
                        ? 'bg-indigo-100/80 text-indigo-800 border border-indigo-200'
                        : user.role === 'ASSOCIATE'
                          ? 'bg-teal-100/80 text-teal-800 border border-teal-200'
                          : user.role === 'FRANCHISE'
                            ? 'bg-blue-100/80 text-blue-800 border border-blue-200'
                            : 'bg-gray-100 text-gray-800 border border-gray-200',
                  )}
                >
                  {getUserRoleDisplay(user)}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase',
                    STATUS_TONE[user.status],
                  )}
                >
                  {user.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                <span className="flex items-center gap-1 truncate">
                  <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  {user.email}
                </span>
                {user.mobile && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    {user.mobile}
                  </span>
                )}
              </div>
            </div>
          </div>

          {ap?.associateId && (
            <div className="shrink-0 flex sm:flex-col sm:items-end justify-between items-center bg-white/80 backdrop-blur-xs sm:bg-transparent px-3 py-1.5 sm:p-0 rounded-lg border sm:border-0 border-gray-200/60">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Associate ID
              </span>
              <span className="font-mono font-bold text-sm text-brand-800 bg-brand-50 px-2.5 py-1 rounded border border-brand-200/70 shadow-2xs">
                {ap.associateId}
              </span>
            </div>
          )}
        </div>

        {/* Content Section: 2 Columns for Associate, Single Column for others */}
        {isAssociate ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
            {/* Left Column: Associate Details (col-span-5) */}
            <div className="lg:col-span-5 space-y-4">
              {/* Profile Card */}
              {ap && (
                <div className="rounded-xl border border-gray-200 bg-white shadow-2xs overflow-hidden">
                  <div className="bg-gray-50/80 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5 text-brand-600" />
                      Profile Details
                    </span>
                    {ap.kycStatus && (
                      <span
                        className={cn(
                          'text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border',
                          ap.kycStatus === 'VERIFIED'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : ap.kycStatus === 'REJECTED'
                              ? 'bg-red-50 text-red-700 border-red-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200',
                        )}
                      >
                        KYC: {ap.kycStatus}
                      </span>
                    )}
                  </div>
                  <div className="p-4 space-y-3 text-xs">
                    <div>
                      <p className="text-[11px] font-medium text-gray-500">Profession</p>
                      <p className="text-gray-900 font-medium mt-0.5">
                        {PROFESSION_LABELS[ap.profession] ?? ap.profession}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-[11px] font-medium text-gray-500">Account Type</p>
                        <p className="text-gray-900 font-medium mt-0.5">
                          {ap.isFranchise ? 'Franchise' : 'Associate'}
                        </p>
                      </div>
                      {ap.outletName && (
                        <div>
                          <p className="text-[11px] font-medium text-gray-500">Outlet</p>
                          <p className="text-gray-900 font-medium mt-0.5">{ap.outletName}</p>
                        </div>
                      )}
                    </div>
                    {(ap.state || ap.district || ap.taluk) && (
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-[11px] font-medium text-gray-500 mb-1.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          Operating Region
                        </p>
                        <div className="bg-gray-50 rounded-lg p-2.5 space-y-1.5 text-gray-700">
                          {ap.taluk && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Taluk:</span>
                              <span className="font-medium text-gray-800">{ap.taluk}</span>
                            </div>
                          )}
                          {ap.district && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">District:</span>
                              <span className="font-medium text-gray-800">{ap.district}</span>
                            </div>
                          )}
                          {ap.state && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">State:</span>
                              <span className="font-medium text-gray-800">{ap.state}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Account Activity Card */}
              <div className="rounded-xl border border-gray-200 bg-white shadow-2xs p-4 text-xs space-y-3">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider block border-b border-gray-100 pb-2">
                  Account Timestamps
                </span>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] font-medium text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-gray-400" />
                      Joined
                    </p>
                    <p className="text-gray-800 font-medium mt-0.5">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3 text-gray-400" />
                      Last Login
                    </p>
                    <p className="text-gray-800 font-medium mt-0.5">
                      {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Client Reviews (col-span-7) */}
            <div className="lg:col-span-7 flex flex-col">
              <div className="rounded-xl border border-gray-200 bg-white shadow-2xs overflow-hidden flex flex-col">
                <div className="bg-gray-50/80 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                      Client Reviews
                    </span>
                    <span className="bg-gray-200 text-gray-700 text-[11px] font-bold px-2 py-0.5 rounded-full">
                      {feedbacks.length}
                    </span>
                  </div>
                  {avgRating !== null && (
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg">
                      <InlineStars value={avgRating} />
                      <span className="text-xs font-bold text-amber-900">
                        {avgRating.toFixed(1)} / 5.0
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-4">
                  {loadingFeedbacks ? (
                    <div className="py-12 text-center text-xs text-gray-500">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-brand-600 border-t-transparent mb-2" />
                      <p>Loading client reviews…</p>
                    </div>
                  ) : feedbacks.length === 0 ? (
                    <div className="py-12 px-4 text-center">
                      <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mx-auto mb-2">
                        <Star className="w-6 h-6 stroke-gray-300" />
                      </div>
                      <p className="text-sm font-semibold text-gray-800">No client reviews yet</p>
                      <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                        Reviews will appear here once this associate completes orders with client ratings.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1.5 focus:outline-none">
                      {feedbacks.map((fb) => {
                        const clientInitials = fb.clientName
                          ? fb.clientName
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()
                          : 'CL';
                        return (
                          <div
                            key={fb.transactionId}
                            className="rounded-lg border border-gray-100 bg-gray-50/50 p-3 hover:bg-white hover:border-gray-200 transition-colors shadow-2xs"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-2.5 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-700 text-[11px] font-bold flex items-center justify-center shrink-0">
                                  {clientInitials}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-900 text-xs truncate">
                                    {fb.clientName || 'Client'}
                                  </p>
                                  <p className="text-[11px] text-gray-500 font-medium truncate">
                                    {fb.serviceName}
                                  </p>
                                </div>
                              </div>
                              <div className="flex shrink-0 flex-col items-end">
                                <InlineStars value={fb.rating} />
                                <p className="mt-0.5 text-[10px] text-gray-400 font-medium">
                                  {fb.completedAt ? new Date(fb.completedAt).toLocaleDateString() : ''}
                                </p>
                              </div>
                            </div>
                            {fb.feedbackDescription && (
                              <div className="mt-2 pl-2.5 border-l-2 border-amber-400 bg-white/90 rounded py-1 px-2 text-xs text-gray-700 italic">
                                &ldquo;{fb.feedbackDescription}&rdquo;
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Non-associate simple view */
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-2xs">
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">
              Account Information
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <p className="text-[11px] font-medium text-gray-500">Email Address</p>
                <p className="text-gray-900 font-medium mt-0.5">{user.email}</p>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500">Account Role</p>
                <p className="text-gray-900 font-medium mt-0.5">{getUserRoleDisplay(user)}</p>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500">Phone Number</p>
                <p className="text-gray-900 font-medium mt-0.5">{user.mobile ?? '—'}</p>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500">Account Status</p>
                <span
                  className={cn(
                    'inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase mt-0.5',
                    STATUS_TONE[user.status],
                  )}
                >
                  {user.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500">Registered Date</p>
                <p className="text-gray-900 font-medium mt-0.5">
                  {new Date(user.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500">Last Login Activity</p>
                <p className="text-gray-900 font-medium mt-0.5">
                  {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never logged in'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ---- Main page ----------------------------------------------------
export function AdminUsersPage() {
  const [tab, setTab] = useState<UserRole | 'ALL'>('ALL');
  const [q, setQ] = useState('');
  const [suspendId, setSuspendId] = useState<Uuid | null>(null);
  const [deleteId, setDeleteId] = useState<Uuid | null>(null);
  const [reason, setReason] = useState('');
  const [detailUser, setDetailUser] = useState<AdminUserRow | null>(null);

  const { data: me } = useMe();
  const session = useAuthStore((s) => s.session);
  const currentUserId = me?.id ?? session?.id;

  const { data, isLoading } = useAdminUsers({
    role: tab === 'ALL' ? undefined : tab,
    q: q.trim() || undefined,
    pageSize: 50,
  });

  const { canEdit, canDelete, isSuperAdmin } = useSectionPermission('USERS');
  const suspend = useSuspendUser();
  const reinstate = useReinstateUser();
  const deleteUser = useDeleteUser();

  const items = data?.items ?? [];

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <h1 className="text-xl font-semibold text-gray-900">Users</h1>
      <p className="text-sm text-gray-600">
        Browse all accounts. Suspend revokes refresh tokens and soft-deletes.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-3 w-full">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email / name / mobile"
          className="w-full md:max-w-md"
        />
      </div>

      <div className="mt-3 flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-smooth py-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap border-b border-gray-200 pb-2">
        {ROLE_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={cn(
              'shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap',
              tab === t.value
                ? 'bg-brand-700 text-white shadow-xs'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200/60 sm:border-0',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <section className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 font-medium whitespace-nowrap">User</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Role</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Status</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Last login</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Joined</th>
                <th className="px-4 py-3 text-right font-medium pr-6 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan={6} className="p-6 text-center text-gray-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-gray-500">No users match.</td></tr>
              ) : items.map((u) => {
                const isTargetSuperAdmin = u.adminRole === 'SUPER_ADMIN';
                const isTargetAdmin = u.role === 'ADMIN' || Boolean(u.adminRole);
                const isSelf = Boolean(currentUserId && u.id === currentUserId);

                // Super Admin can NEVER be suspended or deleted by anyone
                // Non-super admins can NEVER suspend, reinstate, or delete any Admin accounts
                // Users cannot suspend, reinstate, or delete themselves
                const canReinstateThisRow =
                  !isSelf &&
                  !isTargetSuperAdmin &&
                  (isSuperAdmin ? true : !isTargetAdmin && canEdit);

                const canSuspendThisRow =
                  !isSelf &&
                  !isTargetSuperAdmin &&
                  (isSuperAdmin ? true : !isTargetAdmin && canDelete);

                const canDeleteThisRow =
                  !isSelf &&
                  !isTargetSuperAdmin &&
                  (isSuperAdmin ? true : !isTargetAdmin && canDelete);

                return (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        className="text-left hover:underline focus:outline-none"
                        onClick={() => setDetailUser(u)}
                      >
                        <div className="font-medium text-brand-700">
                          {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
                        </div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {getUserRoleDisplay(u)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase', STATUS_TONE[u.status])}>
                        {u.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap pr-6">
                      <div className="inline-flex items-center justify-end gap-2.5 text-xs font-semibold whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setDetailUser(u)}
                          className="text-emerald-600 hover:text-emerald-700 hover:underline transition-colors focus:outline-none"
                        >
                          View
                        </button>
                        {canReinstateThisRow && u.status === 'SUSPENDED' ? (
                          <>
                            <span className="h-3.5 w-px bg-gray-200" aria-hidden="true" />
                            <button
                              type="button"
                              onClick={() => reinstate.mutate(u.id)}
                              className="text-emerald-600 hover:text-emerald-700 hover:underline transition-colors disabled:opacity-50 focus:outline-none"
                              disabled={reinstate.isPending}
                            >
                              {reinstate.isPending ? 'Reinstating…' : 'Reinstate'}
                            </button>
                          </>
                        ) : null}
                        {canSuspendThisRow && u.status !== 'SUSPENDED' ? (
                          <>
                            <span className="h-3.5 w-px bg-gray-200" aria-hidden="true" />
                            <button
                              type="button"
                              onClick={() => { setSuspendId(u.id); setReason(''); }}
                              className="text-amber-600 hover:text-amber-700 hover:underline transition-colors focus:outline-none"
                            >
                              Suspend
                            </button>
                          </>
                        ) : null}
                        {canDeleteThisRow ? (
                          <>
                            <span className="h-3.5 w-px bg-gray-200" aria-hidden="true" />
                            <button
                              type="button"
                              onClick={() => { setDeleteId(u.id); }}
                              className="text-red-600 hover:text-red-700 hover:underline transition-colors disabled:opacity-50 focus:outline-none"
                              disabled={deleteUser.isPending}
                            >
                              Delete
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Associate details dialog */}
      <AssociateDetailsDialog
        user={detailUser}
        onClose={() => setDetailUser(null)}
      />

      {/* Suspend dialog */}
      <Dialog
        open={suspendId !== null}
        onClose={() => setSuspendId(null)}
        title="Suspend user"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSuspendId(null)}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 disabled:bg-amber-600/60 text-white"
              onClick={() =>
                suspendId &&
                suspend.mutate(
                  { id: suspendId, reason: reason.trim() || undefined },
                  { onSuccess: () => setSuspendId(null) },
                )
              }
              disabled={suspend.isPending}
            >
              {suspend.isPending ? 'Suspending…' : 'Suspend Account'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-700">
          Refresh tokens are revoked immediately. Optional reason is recorded in audit logs.
        </p>
        <Input
          className="mt-3"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional)"
        />
        {suspend.isError ? <p className="mt-2 text-sm text-red-600">{apiErrorMessage(suspend.error)}</p> : null}
        {reinstate.isError ? <p className="mt-2 text-sm text-red-600">{apiErrorMessage(reinstate.error)}</p> : null}
      </Dialog>

      {/* Delete dialog */}
      <Dialog
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        title="Delete user account completely"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-600/60 text-white"
              onClick={() =>
                deleteId &&
                deleteUser.mutate(deleteId, {
                  onSuccess: () => setDeleteId(null),
                })
              }
              disabled={deleteUser.isPending}
            >
              {deleteUser.isPending ? 'Deleting…' : 'Delete Completely'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-700">
          Are you sure you want to permanently delete this user account?
        </p>
        <p className="mt-2 text-xs text-red-600 font-semibold">
          Warning: This action is irreversible. The account email will be freed up, but this will fail if they have associated transactions or audit records.
        </p>
        {deleteUser.isError ? (
          <p className="mt-3 text-sm text-red-600">{apiErrorMessage(deleteUser.error)}</p>
        ) : null}
      </Dialog>
    </div>
  );
}
